import type {
  DatasetInstanceConfig,
  SelectionListSelectedItem,
} from './data-builder.store';

export type PickedUoaForTarget = {
  unitOfAnalysis: string;
  unitOfAnalysisSelections: SelectionListSelectedItem[];
  sourceInstanceId: string;
};

type PickLastUsedUoaForTargetArgs = {
  configByInstance: Record<string, DatasetInstanceConfig>;
  orderedInstanceIds: string[];
  availableUoaValues: string[];
  excludeInstanceId?: string;
};

type UoaCandidate = {
  instanceId: string;
  config: DatasetInstanceConfig & { unitOfAnalysis: string };
};

export function pickLastUsedUoaForTarget({
  configByInstance,
  orderedInstanceIds,
  availableUoaValues,
  excludeInstanceId,
}: PickLastUsedUoaForTargetArgs): PickedUoaForTarget | null {
  const matchingCandidates = orderedInstanceIds
    .filter((instanceId) => instanceId !== excludeInstanceId)
    .map((instanceId) => ({
      instanceId,
      config: configByInstance[instanceId],
    }))
    .filter((item): item is UoaCandidate => {
      if (item.config === undefined || item.config.unitOfAnalysis === null) {
        return false;
      }
      return availableUoaValues.includes(item.config.unitOfAnalysis);
    });

  if (matchingCandidates.length === 0) {
    return null;
  }

  const latestTimestamp = matchingCandidates.reduce<number | null>(
    (current, candidate) => {
      if (candidate.config.uoaSetAt === null) {
        return current;
      }
      if (current === null || candidate.config.uoaSetAt > current) {
        return candidate.config.uoaSetAt;
      }
      return current;
    },
    null
  );

  const selectedCandidate =
    latestTimestamp === null
      ? (matchingCandidates[0] ?? null)
      : (matchingCandidates.find(
          (candidate) => candidate.config.uoaSetAt === latestTimestamp
        ) ?? null);

  if (selectedCandidate === null) {
    return null;
  }

  return {
    unitOfAnalysis: selectedCandidate.config.unitOfAnalysis,
    unitOfAnalysisSelections: selectedCandidate.config.unitOfAnalysisSelections,
    sourceInstanceId: selectedCandidate.instanceId,
  };
}
